<script setup>
import InlineSvg from 'vue-inline-svg'
import { onClickOutside } from '@vueuse/core'
import { useTemplateRef, ref, onMounted, nextTick, computed, watch } from 'vue'
import { useAuthStore } from '@/stores/auth.js'
import { useChatStore } from '@/stores/chat.js'
import { useUserStore } from '@/stores/user.js'
import { storeToRefs } from 'pinia'
import { dialogSetRelationship } from '@/api/api.js'
import ErrorMessage from '@/components/Functional/ErrorMessage.vue'
import app from '@/main.js'
import faceSmileHaloSvg from '@/assets/fontawesome/regular/face-smile-halo.svg?inline'
import faceSmilingHandsSvg from '@/assets/fontawesome/regular/face-smiling-hands.svg?inline'
import faceGrinStarsSvg from '@/assets/fontawesome/regular/face-grin-stars.svg?inline'
import faceGrinGongueWinkSvg from '@/assets/fontawesome/regular/face-grin-tongue-wink.svg?inline'
import faceGrinHeartsSvg from '@/assets/fontawesome/regular/face-grin-hearts.svg?inline'
import faceThinkingSvg from '@/assets/fontawesome/regular/face-thinking.svg?inline'
import { filter } from 'lodash'

const props = defineProps({
  chatId: {
    type: String,
    required: true
  }
})

const open = ref(false)
const target = useTemplateRef('target')
const bottom = ref(0)

onClickOutside(
  target,
  event => open.value = false,
  { ignore: ['#relationship-button'] }
)

const chatStore = useChatStore()
const userStore = useUserStore()

const messagesFromUser = computed(() => {
  return filter(chatStore.sortedMessages, (item) => (item.type === 3 || item.type === 0) && item.senderId === userStore.userId)
})

// If user sends first message, show relationship selector after 5 seconds
const unwatch = watch(messagesFromUser, async (value, oldValue) => {
  if (oldValue?.length === 1 && value?.length > 1) {
    await new Promise((resolve) => setTimeout(resolve, 5000))
    open.value = true
    unwatch()
  } else if (value?.length > 2) {
    unwatch()
  }
})

const emojiContainer = useTemplateRef('emojisContainer')
const hasHorizontalScroll = ref(false)

watch(open, async (value) => {
  if (value) {
    await nextTick()

    const el = emojiContainer.value

    if (!el) {
      return
    }

    hasHorizontalScroll.value = el.scrollWidth > el.clientWidth
  } else {
    await new Promise((resolve) => setTimeout(resolve, 200))
    hasHorizontalScroll.value = false
  }
})

onMounted(async () => {
  await nextTick()
  const formEl = document.getElementById('chat-form')
  bottom.value = formEl ? formEl.offsetHeight : 0

  if (chatStore.chatDialog?.relationship) {
    selected.value = chatStore.chatDialog.relationship
  }
})

const selected = ref('')

const relationshipIcons = new Map([
  ['serious', faceSmileHaloSvg],
  ['pen_pal', faceSmilingHandsSvg],
  ['loyal_fan', faceGrinStarsSvg],
  ['romantic', faceGrinHeartsSvg],
  ['flirty', faceGrinGongueWinkSvg],
  ['dont_know', faceThinkingSvg]
])

const relationsshipIcon = computed(() => {
  return relationshipIcons.get(selected.value) || faceThinkingSvg
})

const authStore = useAuthStore()

const {
  options
} = storeToRefs(authStore)

const loading = ref(false)
const errorMessage = ref('')

async function selectRelationship (relationship) {
  if (loading.value) {
    return
  }

  try {
    selected.value = relationship
    loading.value = true

    await dialogSetRelationship({
      userId: Number(props.chatId),
      relationship: relationship
    })

    open.value = false
  } catch (error) {
    errorMessage.value = error.message ? error.message : 'An error occurred while updating relationship.'
  } finally {
    loading.value = false
  }
}

const emit = defineEmits(['clicked'])
</script>

<template>
  <button
    v-if="options.dialogRelationshipEnabled"
    id="relationship-button"
    :disabled="messagesFromUser?.length === 0"
    type="button"
    class="cursor-pointer text-gray-400 hover:text-link disabled:opacity-60 disabled:hover:text-gray-400"
    :class="{ '!text-link': open }"
    @click="open = !open; emit('clicked')">
    <InlineSvg
      :src="relationsshipIcon"
      class="w-6 h-6"/>
  </button>

  <transition name="fade">
    <div
      v-if="open"
      ref="target"
      :style="{ bottom: bottom + 'px' }"
      class="absolute left-0 right-0 z-20 bg-white custom-shadow px-4 pb-3 pt-6 rounded-t-3xl">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-2xl font-bold pl-1">
          {{ app.$i18n.t('dialog.here_i_am') }}
        </h3>

        <button
          class="text-gray-950 p-2 -mr-2"
          type="button"
          @click="open = false">
          <InlineSvg
            :src="require('@/assets/svg/design/cross.svg')"
            class="w-3 h-3"/>
        </button>
      </div>

      <div
        ref="emojisContainer"
        :class="[hasHorizontalScroll ? 'grid-cols-3' : 'grid-flow-col  overflow-x-auto overflow-y-hidden']"
        class="grid text-center whitespace-nowrap pb-1">
        <button
          type="button"
          :class="{ 'bg-primary text-white': selected === 'serious' }"
          class="px-2 pt-2 pb-1.5 rounded-lg flex flex-col items-center justify-center gap-1"
          @click="selectRelationship('serious')">
          <img
            class="block w-8 min-w-8 lg:w-10 lg:min-w-10"
            alt="Smiling Face with Halo"
            src="@/assets/images/emoji/smiling-face-with-halo.png">

          <small class="text-[10px] leading-4 lg:text-caption font-semibold">
            {{ app.$i18n.t('dialog.serious') }}
          </small>
        </button>

        <button
          type="button"
          :class="{ 'bg-primary text-white': selected === 'pen_pal' }"
          class="px-2 pt-2 pb-1.5 rounded-lg flex flex-col items-center justify-center gap-1"
          @click="selectRelationship('pen_pal')">
          <img
            class="block w-8 min-w-8 lg:w-10 lg:min-w-10"
            alt="Hugging Face"
            src="@/assets/images/emoji/hugging-face.png">

          <small class="text-[10px] leading-4 lg:text-caption font-semibold">
            {{ app.$i18n.t('dialog.pen_pal') }}
          </small>
        </button>

        <button
          type="button"
          :class="{ 'bg-primary text-white': selected === 'loyal_fan' }"
          class="px-2 pt-2 pb-1.5 rounded-lg flex flex-col items-center justify-center gap-1"
          @click="selectRelationship('loyal_fan')">
          <img
            class="block w-8 min-w-8 lg:w-10 lg:min-w-10"
            alt="Star-Struck"
            src="@/assets/images/emoji/star-struck.png">

          <small class="text-[10px] leading-4 lg:text-caption font-semibold">
            {{ app.$i18n.t('dialog.loyal_fan') }}
          </small>
        </button>

        <button
          type="button"
          :class="{ 'bg-primary text-white': selected === 'romantic' }"
          class="px-2 pt-2 pb-1.5 rounded-lg flex flex-col items-center justify-center gap-1"
          @click="selectRelationship('romantic')">
          <img
            class="block w-8 min-w-8 lg:w-10 lg:min-w-10"
            alt="Winking Face with Tongue"
            src="@/assets/images/emoji/smiling-face-with-heart-eyes.png">

          <small class="text-[10px] leading-4 lg:text-caption font-semibold">
            {{ app.$i18n.t('dialog.romantic') }}
          </small>
        </button>

        <button
          type="button"
          :class="{ 'bg-primary text-white': selected === 'flirty' }"
          class="px-2 pt-2 pb-1.5 rounded-lg flex flex-col items-center justify-center gap-1"
          @click="selectRelationship('flirty')">
          <img
            class="block w-8 min-w-8 lg:w-10 lg:min-w-10"
            alt="Smiling Face with Heart-Eyes"
            src="@/assets/images/emoji/winking-face-with-tongue.png">

          <small class="text-[10px] leading-4 lg:text-caption font-semibold">
            {{ app.$i18n.t('dialog.flirty') }}
          </small>
        </button>

        <button
          type="button"
          :class="{ 'bg-primary text-white': selected === 'dont_know' }"
          class="px-2 pt-2 pb-1.5 rounded-lg flex flex-col items-center justify-center gap-1"
          @click="selectRelationship('dont_know')">
          <img
            class="block w-8 min-w-8 lg:w-10 lg:min-w-10"
            alt="Thinking Face"
            src="@/assets/images/emoji/thinking-face.png">

          <small class="text-[10px] leading-4 lg:text-caption font-semibold">
            {{ app.$i18n.t('dialog.dont_know') }}
          </small>
        </button>
      </div>

      <ErrorMessage :message="errorMessage"/>
    </div>
  </transition>
</template>

<style scoped>
  .custom-shadow {
    box-shadow: 0px -10px 16px 1px rgba(0, 0, 0, 0.03);
  }
</style>